// src/firebase.js
import { initializeApp } from "firebase/app";
import { 
  getAuth,
  GoogleAuthProvider,
  browserLocalPersistence,
  initializeAuth,
  inMemoryPersistence
} from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from "firebase/app-check";

const firebaseConfig = {
  apiKey: "AIzaSyDxkgsKKoj4FU00AW_1LEFNrbUjkqYHiVU",
  authDomain: "arabic-ai-kids.firebaseapp.com",
  projectId: "arabic-ai-kids",
  storageBucket: "arabic-ai-kids.firebasestorage.app",
  messagingSenderId: "202033787228",
  appId: "1:202033787228:web:0111098ce9c527d141d199"
};

const app = initializeApp(firebaseConfig);

// ─── App Check (Phase 1) ─────────────────────────────────────────────────────
// Provider: reCAPTCHA ENTERPRISE (ReCaptchaEnterpriseProvider). The Firebase
// console for this project no longer offers the classic reCAPTCHA v3 provider
// for web App Check — only Enterprise (score-based, site key with no secret).
// Do NOT re-introduce ReCaptchaV3Provider here.
//
// INERT until REACT_APP_APPCHECK_RECAPTCHA_SITE_KEY is set at build time (the
// value is the reCAPTCHA Enterprise site key), so existing production builds are
// completely unaffected until App Check is provisioned. When active, the SDK
// attaches an App Check token to every Firebase request automatically.
//
// Enforcement is applied PER SERVICE and is deliberately narrow in Phase 1:
// only the createTrialSignup callable enforces it (runWith enforceAppCheck in
// functions/index.js). Firestore and Auth enforcement stay OFF — turning those
// on project-wide could lock out paying customers over a reCAPTCHA hiccup.
const appCheckSiteKey = process.env.REACT_APP_APPCHECK_RECAPTCHA_SITE_KEY;
if (appCheckSiteKey) {
  // Local development only: register an App Check debug token (created in the
  // Firebase console) so the SDK skips the real reCAPTCHA challenge. The debug
  // provider replaces whichever real provider is configured, so this path is
  // independent of Enterprise vs v3.
  //
  // The `process.env.NODE_ENV !== "production"` guard is load-bearing, not
  // cosmetic. `react-scripts build` sets NODE_ENV to "production" and webpack's
  // DefinePlugin inlines it as the literal "production", so this whole block
  // becomes `if (false && ...) { ... }` and Terser strips it — statements,
  // string references and all — from the production bundle. That means the
  // debug token can never be inlined into a shipped bundle no matter how
  // REACT_APP_APPCHECK_DEBUG_TOKEN reaches the build: a stray shell export, a
  // future .env.local, a CI misconfiguration, or a stale webpack cache. A debug
  // token in a public bundle is a permanent App Check bypass, so this must stay.
  //
  // For `npm start` (NODE_ENV "development") the guard is true and the debug
  // path works exactly as before, reading the token from .env.development.local.
  if (
    process.env.NODE_ENV !== "production" &&
    process.env.REACT_APP_APPCHECK_DEBUG_TOKEN
  ) {
    window.FIREBASE_APPCHECK_DEBUG_TOKEN = process.env.REACT_APP_APPCHECK_DEBUG_TOKEN;
  }
  try {
    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(appCheckSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (err) {
    // Never let App Check init break app boot.
    console.error("App Check initialization failed:", err);
  }
}

// Enhanced auth initialization
const auth = initializeAuth(app, {
  persistence: [browserLocalPersistence, inMemoryPersistence]
});

// Force persistence setup
(async () => {
  try {
    await auth.setPersistence(browserLocalPersistence);
  } catch (err) {
    console.error("Persistence setup error:", err);
  }
})();

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });

const storage = getStorage(app);
const db = getFirestore(app);

export { auth, googleProvider, db, storage };